import {ApolloLink, Observable} from "@apollo/client";
import uuid from "uuid";
import {getFaroSessionId} from "./faro";

/**
 * Diagnose-Kontext für „Fehler melden“: Kleine Ringpuffer mit den zuletzt
 * besuchten Seiten, GraphQL-Anfragen und Fehlern, damit eine Meldung zeigt,
 * was kurz davor im Browser passiert ist.
 *
 * Jede Anfrage bekommt eine eigene `x-correlation-id`. Der Server übernimmt sie
 * (loggingContext.ts) und schreibt sie in jede Logzeile, so führt ein Eintrag
 * im Puffer direkt zu den Backend-Logs dieser Anfrage.
 *
 * Bewusst nicht gespeichert: GraphQL-Variablen. Darin stehen Kundendaten, und
 * über die Correlation-ID finden sie sich maskiert in den Server-Logs. Ebenso
 * nicht: Magic-Link- und Passwort-Reset-Codes aus der URL (redactCredentials).
 */

const MAX_ROUTES = 10;
const MAX_OPERATIONS = 30;
const MAX_ERRORS = 20;
const MAX_MESSAGE_LENGTH = 500;
const MAX_STACK_LENGTH = 2000;

export type RouteEntry = {at: string; path: string};

export type OperationEntry = {
    at: string;
    operationName: string;
    correlationId: string;
    durationMs: number;
    ok: boolean;
    error?: string;
};

export type ErrorEntry = {
    at: string;
    source: "uncaught" | "logger";
    message: string;
    stack?: string;
};

export type ReportContext = {
    capturedAt: string;
    url: string;
    buildSha: string;
    appVersion: string;
    deployEnv: string;
    faroSessionId?: string;
    userAgent: string;
    language: string;
    timeZone: string;
    viewport: string;
    routes: Array<RouteEntry>;
    operations: Array<OperationEntry>;
    errors: Array<ErrorEntry>;
};

const routes: Array<RouteEntry> = [];
const operations: Array<OperationEntry> = [];
const errors: Array<ErrorEntry> = [];

const push = <T>(buffer: Array<T>, entry: T, max: number): void => {
    buffer.push(entry);
    if (buffer.length > max) {
        buffer.splice(0, buffer.length - max);
    }
};

const now = () => new Date().toISOString();

/** Pfadsegmente, nach denen Zugangsdaten folgen (`/login/:code`, `/forgot-password/:id/:url`). */
const CREDENTIAL_SEGMENTS = ["login", "forgot-password"];
const REDACTED = "[entfernt]";

/** Schneidet alles hinter einem Zugangsdaten-Segment ab, samt Query-String. */
export const redactCredentials = (path: string): string => {
    const segments = path.split("/");
    const index = segments.findIndex((segment) => CREDENTIAL_SEGMENTS.includes(segment));

    return index === -1 || index === segments.length - 1 ? path : [...segments.slice(0, index + 1), REDACTED].join("/");
};

let currentUserId: string | undefined;

/**
 * Meldet den angemeldeten Benutzer. Wechselt er im selben Tab (Logout, anderer
 * Login), beginnen die Puffer leer, damit keine Meldung Verlauf eines anderen
 * Benutzers enthält.
 */
export const setReportUser = (userId: string | undefined): void => {
    if (userId === currentUserId) {
        return;
    }
    if (currentUserId !== undefined) {
        routes.length = 0;
        operations.length = 0;
        errors.length = 0;
    }
    currentUserId = userId;
};

export const recordRoute = (path: string): void => {
    push(routes, {at: now(), path: redactCredentials(path)}, MAX_ROUTES);
};

export const recordError = (source: ErrorEntry["source"], message: string, stack?: string): void => {
    push(errors, {at: now(), source, message: message.slice(0, MAX_MESSAGE_LENGTH), stack: stack?.slice(0, MAX_STACK_LENGTH)}, MAX_ERRORS);
};

/**
 * Setzt `x-correlation-id` (eine schon gesetzte ID bleibt, z. B. aus
 * ReverseBookingModal) und protokolliert Ergebnis und Dauer. Wiederholt der
 * errorLink eine Anfrage, läuft sie mit derselben ID erneut hier durch.
 */
export const correlationLink = new ApolloLink((operation, forward) => {
    const headers: Record<string, string | undefined> = operation.getContext().headers ?? {};
    const correlationId = headers["x-correlation-id"] ?? uuid();

    operation.setContext({headers: {...headers, "x-correlation-id": correlationId}});

    const startedAt = Date.now();
    const record = (error?: string) =>
        push(
            operations,
            {
                at: new Date(startedAt).toISOString(),
                operationName: operation.operationName || "(unbenannt)",
                correlationId,
                durationMs: Date.now() - startedAt,
                ok: error === undefined,
                error: error?.slice(0, MAX_MESSAGE_LENGTH),
            },
            MAX_OPERATIONS,
        );

    return new Observable((observer) => {
        const subscription = forward(operation).subscribe({
            next: (result) => {
                record(result.errors?.length ? result.errors.map((e) => e.message).join(" | ") : undefined);
                observer.next(result);
            },
            error: (error: unknown) => {
                record(error instanceof Error ? error.message : String(error));
                observer.error(error);
            },
            complete: () => observer.complete(),
        });

        return () => subscription.unsubscribe();
    });
});

export const getReportContext = (): ReportContext => ({
    capturedAt: now(),
    url: window.location.origin + redactCredentials(window.location.pathname + window.location.search),
    buildSha: process.env.BUILD_SHA ?? "unknown",
    appVersion: process.env.APP_VERSION ?? "unknown",
    deployEnv: process.env.DEPLOY_ENV ?? "development",
    faroSessionId: getFaroSessionId(),
    userAgent: navigator.userAgent,
    language: navigator.language,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    routes: [...routes],
    operations: [...operations],
    errors: [...errors],
});
